import React, { useEffect, useRef, useState } from 'react';
import { DotsVerticalIcon, TrashIcon, UserIcon } from '@heroicons/react/outline';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

function initials(user) {
  const first = user?.firstName?.trim()?.[0] || '';
  const last = user?.lastName?.trim()?.[0] || '';
  const init = (first + last).toUpperCase();
  return init || (user?.email?.trim()?.[0] || '?').toUpperCase();
}

function displayName(user) {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  return name || user?.email || user?.id || 'Unknown';
}

function ToDoCard({ todo, statusId, index, category, assignee, onDelete, isDragging }) {
  const rootRef = useRef(null);
  const handleRef = useRef(null);

  const todoIdRef = useRef(todo?.id);
  const statusIdRef = useRef(statusId);
  const indexRef = useRef(index);

  const [isOver, setIsOver] = useState(false);
  const [overEdge, setOverEdge] = useState(null); // 'top' | 'bottom' | null

  useEffect(() => {
    todoIdRef.current = todo?.id;
  }, [todo?.id]);
  useEffect(() => {
    statusIdRef.current = statusId;
  }, [statusId]);
  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    return combine(
      draggable({
        element: el,
        dragHandle: handleRef.current || undefined,
        getInitialData: () => ({
          kind: 'todo-card',
          todoId: todoIdRef.current,
          statusId: statusIdRef.current,
        }),
      }),
      dropTargetForElements({
        element: el,
        canDrop: ({ source }) => source.data?.kind === 'todo-card',
        getData: ({ input, element }) => {
          const rect = element.getBoundingClientRect();
          const edge = input.clientY < rect.top + rect.height / 2 ? 'top' : 'bottom';
          return {
            kind: 'todo-card',
            todoId: todoIdRef.current,
            statusId: statusIdRef.current,
            index: indexRef.current,
            edge,
          };
        },
        onDragEnter: ({ self }) => {
          setIsOver(true);
          setOverEdge(self.data?.edge === 'bottom' ? 'bottom' : 'top');
        },
        onDrag: ({ self }) => {
          setIsOver(true);
          setOverEdge(self.data?.edge === 'bottom' ? 'bottom' : 'top');
        },
        onDragLeave: () => {
          setIsOver(false);
          setOverEdge(null);
        },
        onDrop: () => {
          setIsOver(false);
          setOverEdge(null);
        },
      })
    );
  }, []);

  const categoryLabel = category?.label || '';
  const assigneeLabel = assignee ? displayName(assignee) : 'Unassigned';

  const borderLine =
    isOver && overEdge ? (
      <div
        className={
          'absolute left-2 right-2 h-[2px] rounded ' +
          (overEdge === 'top' ? 'top-1' : 'bottom-1') +
          ' bg-slate-900'
        }
      />
    ) : null;

  return (
    <div
      ref={rootRef}
      className={
        'relative bg-slate-50 border rounded-lg p-2 transition ' +
        (isDragging ? 'opacity-60' : 'hover:bg-slate-100')
      }
    >
      {borderLine}
      <div className="flex items-start gap-2">
        <div
          ref={handleRef}
          className="mt-0.5 p-1 rounded-md text-slate-500 hover:text-slate-800 hover:bg-white/70 cursor-grab active:cursor-grabbing"
          title="Drag"
        >
          <DotsVerticalIcon className="w-5 h-5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm truncate">{todo?.name || '—'}</div>
          {todo?.description ? (
            <div className="text-xs text-neutral-500 mt-0.5 truncate">{todo.description}</div>
          ) : null}

          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {categoryLabel ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-white border">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: category?.color || '#e2e8f0' }}
                />
                <span className="truncate max-w-[10rem]">{categoryLabel}</span>
              </span>
            ) : null}

            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-white border">
              {assignee ? (
                <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-[10px] font-semibold">
                  {initials(assignee)}
                </span>
              ) : (
                <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center">
                  <UserIcon className="w-3.5 h-3.5" />
                </span>
              )}
              <span className="truncate max-w-[10rem]" title={assigneeLabel}>
                {assigneeLabel}
              </span>
            </span>
          </div>
        </div>

        {onDelete && (
          <button
            type="button"
            className="p-2 rounded-lg hover:bg-red-50 text-red-600"
            title="Delete"
            onClick={async () => {
              const ok = window.confirm(`Delete "${todo?.name || 'task'}"?`);
              if (!ok) return;
              await onDelete(todo.id);
            }}
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default ToDoCard;
